import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AuditLogUserParams, AuditType, IAuditLogFilterParams } from "@app/audit-log/models/audit-log-type.model";
import { Subject, takeUntil } from "rxjs";

import { NgChanges } from "@app/shared/model/ng-changes.model";

@Component({
    selector: "app-log-filter",
    templateUrl: "./log-filter.component.html",
    styleUrls: ["./log-filter.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogFilterComponent implements OnInit, OnDestroy, OnChanges {
    @Input() auditTypeList: AuditType[] = [];
    @Input() category = "";
    @Input() userList: AuditLogUserParams[] = [];
    @Input() initStartDate: number;
    @Input() initEndDate: number;

    @Output() logFilterChange = new EventEmitter<IAuditLogFilterParams>();
    @Output() queryChange = new EventEmitter<string>();

    typeList: string[] = [];
    subTypeList: string[] = [];
    queryString = "";
    isFilterExpanded = false;

    filterForm: FormGroup;
    expandedUserList: AuditLogUserParams[] = [];

    unsubscribe$ = new Subject();

    constructor(private readonly fb: FormBuilder) {}
    ngOnChanges(changes: NgChanges<LogFilterComponent>): void {
        this.extractUserList(changes.userList?.currentValue);
    }
    private extractUserList(currentValue: AuditLogUserParams[]) {
        if (this.filterForm?.controls?.["user"]) {
            if (currentValue) {
                const currentUser = currentValue.find(v => v.email === this.filterForm?.controls["user"].value.email);
                if (currentUser) {
                    this.filterForm.patchValue({ user: currentUser });
                } else if (this.filterForm.controls["user"].value) {
                    this.userList = [...currentValue, this.filterForm?.controls["user"].value];
                }
            } else {
                this.userList = [this.filterForm?.controls["user"].value];
            }
        }
    }
    ngOnInit(): void {
        this.filterForm = this.fb.group({
            category: [this.category],
            type: [""],
            subType: [{ value: "", disabled: true }],
            user: [""],
            startTime: [this.initStartDate],
            endTime: [this.initEndDate],
        });
        this.parseTypeList();
        this.filterForm.controls["type"].valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((mainType: string) => {
            this.filterForm.patchValue({ subType: "" });
            this.extractSubTypeList(mainType);
        });
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    /**
     * Update search query string.
     *
     * @param queryString
     * @param queryString.searchTerm
     */
    onSearchingEvents(queryString: { searchTerm: string }) {
        this.queryString = queryString.searchTerm;
        this.queryChange.emit(queryString.searchTerm);
    }

    /**
     * Refresh the stored data.
     */
    onDataRefresh() {
        this.applyFilter();
    }

    applyFilter() {
        const formValue = this.filterForm.getRawValue();
        this.logFilterChange.emit(formValue);
    }

    toggleFilter() {
        this.isFilterExpanded = !this.isFilterExpanded;
    }

    resetFilter() {
        this.filterForm.patchValue({
            category: this.category,
            type: "",
            subType: "",
            user: "",
            startTime: this.initStartDate,
            endTime: this.initEndDate,
        });
        this.queryString = "";
        this.applyFilter();
    }

    resetFilterByField(key: string) {
        if (key === "query") {
            this.queryString = "";
            this.queryChange.emit("");
        } else {
            this.filterForm.patchValue({
                [key]: "",
            });
        }

        this.applyFilter();
    }

    get startTime() {
        return this.filterForm.controls["startTime"].value;
    }

    get endTime() {
        return this.filterForm.controls["endTime"].value;
    }

    /**
     * Select the start date.
     *
     * @param startTime
     */
    onStartDateSelect(startTime: number) {
        this.filterForm.patchValue({ startTime });
    }

    /**
     * Select the end date.
     *
     * @param endTime
     */
    onEndDateSelect(endTime: number) {
        this.filterForm.patchValue({ endTime });
    }

    private parseTypeList() {
        this.typeList = this.auditTypeList.reduce((curr, auditType) => {
            if (curr.findIndex(t => t === auditType.type) < 0) {
                return [...curr, auditType.type];
            }
            return curr;
        }, [] as string[]);
    }

    private extractSubTypeList(mainType: string) {
        this.filterForm.controls["subType"].setValue("");
        if (!mainType) {
            this.filterForm.controls["subType"].disable();
            this.subTypeList = [];

            return;
        }

        const selectedTypes = this.auditTypeList.filter(t => t.type === mainType);
        this.subTypeList = selectedTypes.reduce((curr, auditType) => {
            if (curr.findIndex(t => t === auditType.subType) < 0) {
                return [...curr, auditType.subType];
            }
            return curr;
        }, [] as string[]);
        this.filterForm.controls["subType"].enable();
    }
}
